import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { format, addDays, startOfDay, isSameDay } from 'date-fns';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { FaChevronDown, FaChevronUp, FaChevronLeft, FaChevronRight, FaSort, FaSortUp, FaSortDown, FaCalendarAlt, FaExpandAlt, FaCompressAlt } from 'react-icons/fa';
import api from '../../services/api';
import { useHrPredictions, useSportsbooks } from '../../hooks/useApiQueries';
import { useDeepLinkPlayerExpand } from '../../hooks/useDeepLinkPlayerExpand';
import { useDateQueryParam, getInitialDateForModelPage } from '../../hooks/useDateQueryParam';
import FilterDropdown from './FilterDropdown';
import SportsbookDropdown from './SportsbookDropdown.js';
import FeaturedAndLineupBars from './FeaturedAndLineupBars';
import PlayerWithAvatar from '../PlayerWithAvatar';
import TeamWithLogo from '../TeamWithLogo';
import MatchupCell from './MatchupCell';
import StatusWithGamedayLink from './StatusWithGamedayLink';
import TrackPickButton, { isGameNotStarted } from './TrackPickButton';
import ModelInsightsLockedTeaser from './ModelInsightsLockedTeaser';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import ShapRadarChart from './ShapRadarChart';
import SportsbookLogo from '../SportsbookLogo';
import OddsEdgeMobileSortBar from './OddsEdgeMobileSortBar';
import { americanOddsDisplayToSortNumber, compareNullableNumbersForSort } from '../../utils/predictionSortHelpers';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  @media (max-width: 600px) {
    max-width: 100vw;
    padding: 0.5rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: ${props => props.theme.primaryText};
  margin-bottom: 0.5rem;
  transition: color 0.3s ease;
`;

const Subtitle = styled.h2`
  font-size: 1.25rem;
  color: ${props => props.theme.secondaryText};
  font-weight: 500;
  transition: color 0.3s ease;
`;

const DatePickerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  gap: 1rem;
  margin-bottom: 2rem;
  @media (min-width: 769px) {
    justify-content: flex-start;
    margin-bottom: 0;
  }
`;

const DateLastUpdatedRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 1rem;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }
`;

const DateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => props.theme.inputBg};
  border: 1.5px solid ${props => props.theme.primary};
  border-radius: 0.4rem;
  padding: 0.45rem 0.9rem;
  font-size: 0.95rem;
  color: ${props => props.theme.primaryText};
  cursor: pointer;
  box-shadow: ${props => props.theme.shadow};
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: fit-content;
  
  &:hover, &:focus {
    border: 1.5px solid ${props => props.theme.primaryHover};
    outline: none;
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.5rem 0.8rem;
    gap: 0.5rem;
    min-width: 120px;
  }
  
  @media (max-width: 600px) {
    min-width: 2.5rem;
    width: 2.5rem;
    padding: 0;
    gap: 0;
    height: 2.5rem;
    min-height: 2.5rem;
    justify-content: center;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    min-width: 2.5rem;
    width: 2.5rem;
    padding: 0;
    height: 2.5rem;
    min-height: 2.5rem;
    justify-content: center;
  }
`;

const DateButtonValue = styled.span`
  @media (max-width: 600px) {
    display: none;
  }
`;

const CalendarIcon = styled(FaCalendarAlt)`
  color: ${props => props.theme.primary};
  font-size: 1.2em;
  
  @media (max-width: 768px) {
    font-size: 1em;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9em;
  }
`;

const ChevronIcon = styled(FaChevronDown)`
  color: ${props => props.theme.mutedText};
  font-size: 1em;
  
  @media (max-width: 768px) {
    font-size: 0.9em;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8em;
  }
`;

const DateButtonChevron = styled(ChevronIcon)`
  @media (max-width: 600px) {
    display: none;
  }
`;

const DateNavWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  @media (max-width: 600px) { gap: 0.2rem; }
`;

const DateNavBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.inputBg};
  border: 1.5px solid ${props => props.theme.primary};
  border-radius: 0.4rem;
  color: ${props => props.theme.primary};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.5 : 1};
  padding: 0.3rem;
  min-width: 1.75rem;
  height: 1.75rem;
  &:hover:not(:disabled), &:focus { border-color: ${props => props.theme.primaryHover}; outline: none; }
  &:disabled { cursor: not-allowed; }
  @media (max-width: 600px) {
    min-width: 2.25rem; width: 2.25rem; height: 2.25rem; padding: 0;
  }
`;

// Custom input for react-datepicker (calendar icon only on mobile)
const DateButtonInput = React.forwardRef(({ value, onClick }, ref) => {
  const { theme } = useTheme();
  return (
    <DateButton type="button" onClick={onClick} ref={ref} theme={theme} aria-label={`Select date. Current: ${value}`}>
      <CalendarIcon theme={theme} />
      <DateButtonValue>{value}</DateButtonValue>
      <DateButtonChevron theme={theme} />
    </DateButton>
  );
});

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 2rem;
  box-shadow: ${props => props.theme.shadow};
  border-radius: 0.5rem;
  overflow: hidden;
  font-size: 1rem;
  background-color: ${props => props.theme.cardBg};
  transition: all 0.3s ease;
  @media (max-width: 600px) {
    font-size: 0.875rem;
  }
`;

const TableHead = styled.thead`
  background-color: ${props => props.theme.primary};
  color: ${props => props.theme.inverseText};
  th {
    padding: 0.65rem 0.75rem;
    text-align: left;
    font-weight: 500;
    font-size: 0.9rem;
    @media (max-width: 600px) {
      padding: 0.42rem 0.5rem;
      font-size: 0.8rem;
    }
  }
`;

const TableBody = styled.tbody`
  tr {
    &:nth-child(odd) {
      background-color: ${props => props.theme.tableRowBg};
    }
    &:nth-child(even) {
      background-color: ${props => props.theme.tableRowHoverBg};
    }
    &:hover {
      background-color: ${props => props.theme.tertiaryBg};
    }
  }
  td {
    padding: 0.5rem 0.65rem;
    border-bottom: 1px solid ${props => props.theme.border};
    color: ${props => props.theme.primaryText};
    font-size: 0.9rem;
    transition: all 0.3s ease;
    @media (max-width: 600px) {
      padding: 0.35rem 0.5rem;
      font-size: 0.8125rem;
    }

    &.value {
      font-weight: 600;
      color: ${props => props.theme.primaryText};
    }
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.25rem;
  color: ${props => props.theme.secondaryText};
  transition: color 0.3s ease;
`;

const ErrorMessage = styled.div`
  background-color: ${props => props.theme.error};
  border-left: 4px solid ${props => props.theme.error};
  padding: 1rem;
  border-radius: 0.25rem;
  margin-bottom: 2rem;
  color: ${props => props.theme.inverseText};
  transition: all 0.3s ease;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 3rem;
  background-color: ${props => props.theme.secondaryBg};
  border-radius: 0.5rem;
  border: 1px dashed ${props => props.theme.border};
  transition: all 0.3s ease;
  
  h3 {
    color: ${props => props.theme.primaryText};
    margin-bottom: 0.5rem;
  }
  
  p {
    color: ${props => props.theme.secondaryText};
  }
`;

// Add this styled component for highlighting actual values
const ActualValue = styled.span`
  font-weight: 600;
  color: ${props => props.theme?.secondaryText ?? '#4a5568'};
  ${props => props.exceededPrediction && `
    color: ${props.theme?.success ?? '#2f855a'};
  `}
`;

// Update the LastUpdated styled component to be more visible
const LastUpdated = styled.div`
  text-align: right;
  color: ${props => props.theme.secondaryText};
  font-size: 0.75rem;
  margin: -0.25rem 0 0.5rem 0;
  padding: 0.5rem;
  font-style: italic;
  background-color: ${props => props.theme.secondaryBg};
  border-radius: 4px;
  white-space: nowrap;  // Prevent wrapping
  overflow: hidden;
  text-overflow: ellipsis;  // Show ellipsis if text is too long
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    font-size: 0.7rem;  // Slightly smaller on mobile
  }
`;

// Add this new styled component near the other styled components
const LineupStatus = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  
  ${props => props.status === 'in_lineup' && `
    background-color: #C6F6D5;
    color: #2F855A;
  `}
  
  ${props => props.status === 'not_in_lineup' && `
    background-color: #FED7D7;
    color: #C53030;
  `}
  
  ${props => props.status === 'pending' && `
    background-color: #EDF2F7;
    color: #718096;
  `}
`;



const CardList = styled.div`
  display: none;
  @media (max-width: 600px) {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }
`;

const PredictionCard = styled.div`
  background: ${props => props.theme.cardBg};
  border-radius: 0.5rem;
  box-shadow: ${props => props.theme.shadow};
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: relative;
  transition: all 0.3s ease;
  @media (max-width: 600px) {
    padding: 0.55rem 0.6rem 0.6rem;
    gap: 0.3rem;
  }
`;

const CardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.05rem;
  @media (max-width: 600px) {
    font-size: 0.875rem;
  }
`;

const CardExpandBtn = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.primary};
  font-size: 1.1rem;
  cursor: pointer;
  align-self: flex-end;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: color 0.3s ease;
  @media (max-width: 600px) {
    font-size: 0.8125rem;
    gap: 0.15rem;
  }
`;

const CardDetails = styled.div`
  margin-top: 0.5rem;
  background: ${props => props.theme.tertiaryBg};
  border-radius: 0.25rem;
  padding: 0.75rem 0.5rem 0.5rem 0.5rem;
  font-size: 0.98rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  transition: background-color 0.3s ease;
  @media (max-width: 600px) {
    margin-top: 0.25rem;
    padding: 0.45rem 0.45rem 0.35rem;
    font-size: 0.8125rem;
    gap: 0.2rem;
  }
`;

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

const DetailValue = styled.span`
  display: inline-flex;
  align-items: center;
`;

const DesktopTableWrapper = styled.div`
  display: block;
  @media (max-width: 600px) {
    display: none;
  }
`;

const ExpandAllBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => props.theme.inputBg};
  border: 1.5px solid ${props => props.theme.primary};
  border-radius: 0.4rem;
  padding: 0.45rem 0.9rem;
  font-size: 0.95rem;
  color: ${props => props.theme.primaryText};
  cursor: pointer;
  box-shadow: ${props => props.theme.shadow};
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: fit-content;
  
  &:hover, &:focus {
    border: 1.5px solid ${props => props.theme.primaryHover};
    outline: none;
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.5rem 0.8rem;
    gap: 0.5rem;
    min-width: 120px;
  }
  
  @media (max-width: 600px) {
    min-width: 2.5rem;
    width: 2.5rem;
    padding: 0;
    gap: 0;
    height: 2.5rem;
    min-height: 2.5rem;
    justify-content: center;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    min-width: 2.5rem;
    width: 2.5rem;
    padding: 0;
    height: 2.5rem;
    min-height: 2.5rem;
    justify-content: center;
  }
`;

const ExpandAllLabel = styled.span`
  @media (max-width: 600px) {
    display: none;
  }
`;

const VisualizationsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  gap: 2.5rem;
  margin-bottom: 1rem;
`;

const ThFlex = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3em;
  white-space: nowrap;
`;

const HRPredictions = () => {
  const { theme } = useTheme();
  const { user, isAuthenticated } = useAuth();
  const [selectedDate, setSelectedDate] = useState(getInitialDateForModelPage);
  const [sportsbook, setSportsbook] = useState('best');
  const [filters, setFilters] = useState({
    inLineup: true,
    featuredPicks: true,
    top5: false,
    hasntStarted: false,
    hasntEnded: false
  });
  const [playerSearch, setPlayerSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [searchParams] = useSearchParams();
  useDateQueryParam(searchParams, setSelectedDate);
  const [expandedRows, setExpandedRows] = useState([]);

  const [sortColumn, setSortColumn] = useState('predicted_hr_prob');
  const [sortDirection, setSortDirection] = useState('desc');
  const [featuredCriteria, setFeaturedCriteria] = useState(null);
  const [criteriaError, setCriteriaError] = useState(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  
  // Format the date as YYYY-MM-DD
  const formatDateForApi = (date) => {
    return format(date, 'yyyy-MM-dd');
  };
  
  useEffect(() => {
    if (user?.preferredSportsbook) setSportsbook(user.preferredSportsbook);
  }, [user?.preferredSportsbook]);

  const { data: sportsbooksResp } = useSportsbooks();
  const sportsbooks = sportsbooksResp?.sportsbooks || [];

  const formattedDate = formatDateForApi(selectedDate);
  const { data: predictionsResp, isLoading: loading, error: queryError } = useHrPredictions(
    formattedDate,
    false,
    sportsbook,
  );
  const predictions = predictionsResp?.predictions || [];
  const lastUpdated = predictionsResp?.lastUpdated || null;
  const error = queryError ? `Failed to load predictions: ${queryError.message}` : null;
  
  useEffect(() => {
    api.get('featured-criteria')
      .then(res => setFeaturedCriteria(res.data))
      .catch(err => {
        setCriteriaError('Could not load featured pick criteria.');
        console.error('Failed to load featured criteria:', err);
      });
  }, []);

  const HR_FEATURED_CRITERIA = featuredCriteria?.mlb_hr;
  const getBattingOrderHR = (pick) => Number(pick.battingOrder ?? pick.batting_order ?? -1);
  const getConfidenceHR = (pick) => {
    const v = pick.predicted_hr_prob ?? pick.predictedHRProb;
    return v != null && v !== '' ? Number(v) : null;
  };
  // When there are no featured picks for this model, auto-disable Featured Picks filter
  useEffect(() => {
    const deepPlayer = searchParams.get('player');
    if (deepPlayer != null && String(deepPlayer).trim() !== '') return;
    if (!HR_FEATURED_CRITERIA || !predictions.length) return;
    const featuredCount = predictions.filter(
      (pick) =>
        (getConfidenceHR(pick) ?? Number(pick.predictedHR ?? pick.predicted_hr) ?? -1) >= HR_FEATURED_CRITERIA.pred_threshold &&
        getBattingOrderHR(pick) > (HR_FEATURED_CRITERIA.bat_threshold ?? 0)
    ).length;
    if (featuredCount === 0) {
      setFilters((prev) => (prev.featuredPicks || !prev.top5 ? { ...prev, featuredPicks: false, top5: true } : prev));
    }
  }, [predictions, featuredCriteria, searchParams]);

  const handleDateChange = (date) => {
    console.log(`Date changed to: ${formatDateForApi(date)}`);
    setSelectedDate(date);
  };
  
  const formatGameTime = (isoString) => {
    if (!isoString) return 'TBD';
    
    try {
      const date = new Date(isoString);
      return format(date, 'h:mm a');
    } catch (e) {
      return 'Invalid Time';
    }
  };
  
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const getOddsDisplay = (pick) => {
    if (pick?.hr_odds_display?.display != null) {
      const d = pick.hr_odds_display;
      return `${d.display} (${d.provider})`;
    }
    return null;
  };
  const getOddsValueAndProvider = (pick) => {
    if (pick?.hr_odds_display?.display != null) {
      const d = pick.hr_odds_display;
      return { value: d.display, provider: d.provider };
    }
    return null;
  };
  const renderOddsWithIcon = (pick) => {
    const odds = getOddsValueAndProvider(pick);
    if (!odds) return '-';
    return (
      <>
        <SportsbookLogo provider={odds.provider} size="1.05em" />
        <span>{odds.value}</span>
      </>
    );
  };
  const getDefaultOddsDisplay = (pick) => pick?.hr_odds_display?.display ?? undefined;
  const formatEdgeDisplay = (edgeDisplay) => {
    if (edgeDisplay?.value == null) return null;
    const pct = (Number(edgeDisplay.value) * 100).toFixed(1);
    const sign = Number(edgeDisplay.value) >= 0 ? '+' : '';
    return `${sign}${pct}%`;
  };
  const getConfidence = (pick) => {
    const v = pick.predicted_hr_prob ?? pick.predictedHRProb;
    return v != null && v !== '' ? Number(v) : null;
  };
  const getBattingOrder = (pick) => Number(pick.battingOrder ?? pick.batting_order ?? -1);
  const teamOptions = React.useMemo(() => {
    const set = new Set();
    predictions.forEach((p) => {
      const t = p.teamName ?? p.team_name ?? '';
      if (t && String(t).trim()) set.add(String(t).trim());
    });
    return Array.from(set).sort();
  }, [predictions]);
  const sortedPredictions = [...predictions]
    .filter(pick => {
      const name = (pick.playerName ?? pick.player_name ?? '').toString().toLowerCase();
      const search = playerSearch.trim().toLowerCase();
      if (search && !name.includes(search)) return false;
      const team = (pick.teamName ?? pick.team_name ?? '').toString().trim();
      if (teamFilter.trim() && team !== teamFilter.trim()) return false;
      const inLineup = getBattingOrder(pick) > 0;
      const passesLineupFilter = !filters.inLineup || inLineup;
      if (!HR_FEATURED_CRITERIA) return passesLineupFilter;
      const hrScore = getConfidence(pick) ?? Number(pick.predictedHR ?? pick.predicted_hr ?? -1);
      const passesPredictionFilter = !filters.featuredPicks || (hrScore >= HR_FEATURED_CRITERIA.pred_threshold);
      const passesNotStartedFilter = !filters.hasntStarted || ['Scheduled', 'Pre-Game', 'Preview', 'Warmup'].includes(pick.gameStatus);
      const passesNotEndedFilter = !filters.hasntEnded || (pick.gameStatus !== 'Final' && pick.gameStatus !== 'Game Over');
      return passesLineupFilter && passesPredictionFilter && passesNotStartedFilter && passesNotEndedFilter;
    })
    .sort((a, b) => {
      if (sortColumn === 'odds') {
        const aNum = americanOddsDisplayToSortNumber(a.hr_odds_display?.display);
        const bNum = americanOddsDisplayToSortNumber(b.hr_odds_display?.display);
        return compareNullableNumbersForSort(aNum, bNum, sortDirection);
      }
      if (sortColumn === 'edge') {
        const aRaw = a.hr_edge_display?.value;
        const bRaw = b.hr_edge_display?.value;
        const aNum = aRaw != null && aRaw !== '' ? Number(aRaw) : null;
        const bNum = bRaw != null && bRaw !== '' ? Number(bRaw) : null;
        return compareNullableNumbersForSort(
          Number.isFinite(aNum) ? aNum : null,
          Number.isFinite(bNum) ? bNum : null,
          sortDirection,
        );
      }
      let aValue = sortColumn === 'predicted_hr_prob' ? (getConfidence(a) ?? -1) : a[sortColumn];
      let bValue = sortColumn === 'predicted_hr_prob' ? (getConfidence(b) ?? -1) : b[sortColumn];
      // Fallback for undefined/null
      if (aValue === undefined || aValue === null) aValue = '';
      if (bValue === undefined || bValue === null) bValue = '';
      // Special handling for gameTime (sort by date)
      if (sortColumn === 'gameTime') {
        const aDate = aValue ? new Date(aValue) : new Date(0);
        const bDate = bValue ? new Date(bValue) : new Date(0);
        if (aDate < bDate) return sortDirection === 'asc' ? -1 : 1;
        if (aDate > bDate) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      }
      // Numeric sort if possible
      if (!isNaN(parseFloat(aValue)) && !isNaN(parseFloat(bValue))) {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      } else {
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
      }
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  const displayedPredictions = filters.top5 ? sortedPredictions.slice(0, 5) : sortedPredictions;

  useDeepLinkPlayerExpand({
    searchParams,
    setPlayerSearch,
    setFilters,
    setExpandedRows,
    loading,
    displayedPredictions,
    setTeamFilter,
  });

  // Update the formatLastUpdated function to handle UTC conversion to local timezone
  const formatLastUpdated = (isoString) => {
    if (!isoString) return null;
    try {
      const date = new Date(isoString);
      // Format in user's local timezone
      return format(date, 'MMM d, yyyy h:mm a zzz');  // Added zzz to show timezone
    } catch (e) {
      return isoString;  // Fall back to the raw string if parsing fails
    }
  };
  
  const handleExpandRow = (index) => {
    setExpandedRows(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <Title theme={theme}>MLB Home Runs</Title>
          <Subtitle theme={theme}>{format(selectedDate, 'EEEE, MMMM d, yyyy')}</Subtitle>
          {lastUpdated && (
            <LastUpdated theme={theme} style={{ marginTop: '0.5rem' }}>
              Last updated: {formatLastUpdated(lastUpdated)}
            </LastUpdated>
          )}
        </Header>
        <LoadingMessage theme={theme}>Loading predictions...</LoadingMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title theme={theme}>MLB Home Runs</Title>
        <Subtitle theme={theme}>{format(selectedDate, 'EEEE, MMMM d, yyyy')}</Subtitle>
        {lastUpdated && (
          <LastUpdated theme={theme} style={{ marginTop: '0.5rem', textAlign: 'center', display: 'block' }}>
            Last updated: {formatLastUpdated(lastUpdated)}
          </LastUpdated>
        )}
      </Header>
      <VisualizationsRow>
        {!HR_FEATURED_CRITERIA ? (
          <div>Loading featured pick criteria...</div>
        ) : (
          <FeaturedAndLineupBars
            predictions={predictions}
            featured={predictions.filter(pick => (getConfidence(pick) ?? Number(pick.predictedHR ?? pick.predicted_hr) ?? -1) >= HR_FEATURED_CRITERIA.pred_threshold && getBattingOrder(pick) > (HR_FEATURED_CRITERIA.bat_threshold ?? 0))}
            featuredHitThreshold={1}
          />
        )}
      </VisualizationsRow>
      <div style={{ width: '100%' }}>
        <DateLastUpdatedRow>
                  <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          alignItems: 'center', 
          justifyContent: 'center',
          position: 'relative',
          zIndex: 1
        }}>
          <DateNavWrap>
            <DateNavBtn type="button" theme={theme} onClick={() => { handleDateChange(addDays(selectedDate, -1)); setIsDatePickerOpen(false); }} aria-label="Previous day">
              <FaChevronLeft aria-hidden />
            </DateNavBtn>
            <div style={{ position: 'relative' }}>
            <DatePicker
              selected={selectedDate}
              onChange={(date) => {
                handleDateChange(date);
                setIsDatePickerOpen(false);
              }}
              dateFormat="yyyy-MM-dd"
              customInput={<DateButtonInput />}
              popperPlacement="bottom-start"
              open={isDatePickerOpen}
              onClickOutside={() => setIsDatePickerOpen(false)}
              onInputClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
            />
            </div>
            <DateNavBtn
              type="button"
              theme={theme}
              $disabled={isSameDay(selectedDate, startOfDay(new Date()))}
              disabled={isSameDay(selectedDate, startOfDay(new Date()))}
              onClick={() => { handleDateChange(addDays(selectedDate, 1)); setIsDatePickerOpen(false); }}
              aria-label="Next day"
            >
              <FaChevronRight aria-hidden />
            </DateNavBtn>
          </DateNavWrap>
          <FilterDropdown
            filters={filters}
            onFilterChange={handleFilterChange}
            playerSearch={playerSearch}
            onPlayerSearchChange={setPlayerSearch}
            teamFilter={teamFilter}
            onTeamFilterChange={setTeamFilter}
            teamOptions={teamOptions}
          />
          {user && (
            <SportsbookDropdown options={sportsbooks} value={sportsbook} onChange={setSportsbook} />
          )}
          {displayedPredictions.length > 0 && (
            <ExpandAllBtn
              theme={theme}
              onClick={() => {
                if (expandedRows.length === displayedPredictions.length) {
                  setExpandedRows([]);
                } else {
                  setExpandedRows(displayedPredictions.map((_, idx) => idx));
                }
              }}
              aria-label={expandedRows.length === displayedPredictions.length ? 'Collapse all' : 'Expand all'}
            >
              {expandedRows.length === displayedPredictions.length ? (
                <FaCompressAlt aria-hidden />
              ) : (
                <FaExpandAlt aria-hidden />
              )}
              <ExpandAllLabel>
                {expandedRows.length === displayedPredictions.length ? 'Collapse All' : 'Expand All'}
              </ExpandAllLabel>
            </ExpandAllBtn>
          )}
        </div>
        {user && displayedPredictions.length > 0 && (
          <OddsEdgeMobileSortBar
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            probabilityColumn="predicted_hr_prob"
          />
        )}
        </DateLastUpdatedRow>
      </div>
      {error && (
        <ErrorMessage theme={theme}>{error}</ErrorMessage>
      )}
      {criteriaError && <ErrorMessage theme={theme}>{criteriaError}</ErrorMessage>}
      {!loading && displayedPredictions.length === 0 && (
        <NoDataMessage theme={theme}>
          <h3>No predictions available</h3>
          <p>HR predictions for this date are not yet available.</p>
          <p>Check back later when lineups are posted.</p>
        </NoDataMessage>
      )}
      <>
        {/* Card/List view for mobile */}
        <CardList>
        {displayedPredictions.map((pick, index) => {
          const isExpanded = expandedRows.includes(index);
          return (
            <PredictionCard key={index} theme={theme}>
              <CardRow>
                <PlayerWithAvatar playerId={pick.playerId} playerName={pick.playerName} size={24} />
                <CardExpandBtn theme={theme} onClick={() => handleExpandRow(index)} aria-label={isExpanded ? 'Collapse details' : 'Expand details'}>
                  {isExpanded ? <FaChevronUp /> : <FaChevronDown />} {isExpanded ? 'Less' : 'More'}
                </CardExpandBtn>
              </CardRow>
              <CardRow>
                <span>HR probability:</span>
                <span className="value">{getConfidence(pick) != null ? `${(getConfidence(pick) * 100).toFixed(1)}%` : '-'}</span>
              </CardRow>
              {user && (
                <>
                  <CardRow>
                    <span>Odds:</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', whiteSpace: 'nowrap' }}>
                      {renderOddsWithIcon(pick)}
                    </span>
                  </CardRow>
                  <CardRow>
                    <span>Edge:</span>
                    <span>{formatEdgeDisplay(pick.hr_edge_display) || '-'}</span>
                  </CardRow>
                </>
              )}
              <CardRow>
                <span>Actual HR:</span>
                <span>
                  {pick.actualValue !== undefined && pick.actualValue !== '' ? (
                    <ActualValue theme={theme} exceededPrediction={Number(pick.actualValue) >= 1}>
                      {pick.actualValue}{Number(pick.actualValue) >= 1 ? ' ✅' : ''}
                    </ActualValue>
                  ) : (
                    '-'
                  )}
                </span>
              </CardRow>
              <CardRow>
                <span>Game Time:</span>
                <span>{formatGameTime(pick.gameTime)}</span>
              </CardRow>
              {isExpanded && (
                <CardDetails theme={theme}>
                  <DetailRow><strong>Team:</strong> <DetailValue><TeamWithLogo teamId={pick.teamId} teamName={pick.teamName} size={22} /></DetailValue></DetailRow>
                  <DetailRow><strong>Matchup:</strong> <DetailValue><MatchupCell homeAway={pick.homeAway} opponentTeamId={pick.opponentTeamId} opponent={pick.opponent} /></DetailValue></DetailRow>
                  <DetailRow><strong>Pitcher:</strong> <DetailValue><PlayerWithAvatar playerId={pick.pitcherId} playerName={pick.pitcherName || '-'} size={22} />{pick.pitcherHand ? ` (${pick.pitcherHand})` : ''}</DetailValue></DetailRow>
                  <DetailRow><strong>Summary:</strong> <DetailValue>{pick.summary || '-'}</DetailValue></DetailRow>
                  {isGameNotStarted(pick.gameStatus) && (
                    <div style={{ marginTop: '0.5rem' }} onClick={(e) => e.stopPropagation()}>
                      <TrackPickButton
                        pick={pick}
                        gameDate={formatDateForApi(selectedDate)}
                        pickType="hr"
                        predictionValue={getConfidence(pick) ?? undefined}
                        oddsDisplay={getOddsDisplay(pick) || undefined}
                        defaultOddsDisplay={getDefaultOddsDisplay(pick)}
                        edgeDisplay={formatEdgeDisplay(pick.hr_edge_display) || undefined}
                      />
                    </div>
                  )}
                  <DetailRow><strong>Game Status:</strong> <DetailValue><StatusWithGamedayLink status={pick.gameStatus} gameId={pick.gameId} /></DetailValue></DetailRow>
                  {pick.shap && isAuthenticated && (
                    <div style={{ marginTop: '1rem' }}>
                      <ShapRadarChart shapData={pick.shap} playerName={pick.playerName} forceBarLayout compact compactMaxFeatures={6} labelMaxLength={28} />
                    </div>
                  )}
                  {pick.shap && !isAuthenticated && (
                    <ModelInsightsLockedTeaser playerName={pick.playerName} variant="card" />
                  )}
                </CardDetails>
              )}
            </PredictionCard>
          );
        })}
      </CardList>
      {/* Desktop table view */}
      <DesktopTableWrapper>
        {displayedPredictions.length > 0 && (
            <Table theme={theme}>
              <TableHead theme={theme}>
                <tr>
                  <th></th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('playerName')}>
                    <ThFlex>
                      Player {sortColumn === 'playerName' ? (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort style={{ opacity: 0.4 }} />}
                    </ThFlex>
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('predicted_hr_prob')}>
                    <ThFlex>
                      HR probability {sortColumn === 'predicted_hr_prob' ? (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort style={{ opacity: 0.4 }} />}
                    </ThFlex>
                  </th>
                  {user && (
                    <>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('odds')}>
                        <ThFlex>
                          Odds {sortColumn === 'odds' ? (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort style={{ opacity: 0.4 }} />}
                        </ThFlex>
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('edge')}>
                        <ThFlex>
                          Edge {sortColumn === 'edge' ? (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort style={{ opacity: 0.4 }} />}
                        </ThFlex>
                      </th>
                    </>
                  )}
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('actualValue')}>
                    <ThFlex>
                      Actual HR {sortColumn === 'actualValue' ? (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort style={{ opacity: 0.4 }} />}
                    </ThFlex>
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('gameTime')}>
                    <ThFlex>
                      Game Time {sortColumn === 'gameTime' ? (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort style={{ opacity: 0.4 }} />}
                    </ThFlex>
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('teamName')}>
                    <ThFlex>
                      Team {sortColumn === 'teamName' ? (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort style={{ opacity: 0.4 }} />}
                    </ThFlex>
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('summary')}>
                    <ThFlex>
                      Summary {sortColumn === 'summary' ? (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort style={{ opacity: 0.4 }} />}
                    </ThFlex>
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('gameStatus')}>
                    <ThFlex>
                      Status {sortColumn === 'gameStatus' ? (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort style={{ opacity: 0.4 }} />}
                    </ThFlex>
                  </th>
                </tr>
              </TableHead>
              <TableBody theme={theme}>
                {displayedPredictions.map((pick, index) => {
                  const isExpanded = expandedRows.includes(index);
                  return (
                    <React.Fragment key={index}>
                      <tr>
                        <td style={{ cursor: 'pointer', width: 32 }} onClick={() => handleExpandRow(index)}>
                          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </td>
                        <td><PlayerWithAvatar playerId={pick.playerId} playerName={pick.playerName} size={28} /></td>
                        <td className="value">
                          {getConfidence(pick) != null ? `${(getConfidence(pick) * 100).toFixed(1)}%` : '-'}
                        </td>
                        {user && <>
                          <td>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', whiteSpace: 'nowrap' }}>
                              {renderOddsWithIcon(pick)}
                            </span>
                          </td>
                          <td>{formatEdgeDisplay(pick.hr_edge_display) || '-'}</td>
                        </>}
                        <td>
                          {pick.actualValue !== undefined && pick.actualValue !== '' ? (
                            <ActualValue theme={theme} exceededPrediction={Number(pick.actualValue) >= 1}>
                              {pick.actualValue}{Number(pick.actualValue) >= 1 ? ' ✅' : ''}
                            </ActualValue>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>{formatGameTime(pick.gameTime)}</td>
                        <td><TeamWithLogo teamId={pick.teamId} teamName={pick.teamName} size={24} /></td>
                        <td>{pick.summary || '-'}</td>
                        <td><StatusWithGamedayLink status={pick.gameStatus} gameId={pick.gameId} /></td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={user ? 10 : 8} style={{ background: theme.tableRowHoverBg, padding: 0 }}>
                            <div style={{ padding: '0.75rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', color: theme.primaryText }}>
                              <DetailRow><strong>Matchup:</strong> <DetailValue><MatchupCell homeAway={pick.homeAway} opponentTeamId={pick.opponentTeamId} opponent={pick.opponent} /></DetailValue></DetailRow>
                              <DetailRow><strong>Pitcher:</strong> <DetailValue><PlayerWithAvatar playerId={pick.pitcherId} playerName={pick.pitcherName || '-'} size={22} />{pick.pitcherHand ? ` (${pick.pitcherHand})` : ''}</DetailValue></DetailRow>
                              {isGameNotStarted(pick.gameStatus) && (
                                <div style={{ marginTop: '0.25rem' }} onClick={(e) => e.stopPropagation()}>
                                  <TrackPickButton
                                    pick={pick}
                                    gameDate={formatDateForApi(selectedDate)}
                                    pickType="hr"
                                    predictionValue={getConfidence(pick) ?? undefined}
                                    oddsDisplay={getOddsDisplay(pick) || undefined}
                                    defaultOddsDisplay={getDefaultOddsDisplay(pick)}
                                    edgeDisplay={formatEdgeDisplay(pick.hr_edge_display) || undefined}
                                  />
                                </div>
                              )}
                              {pick.shap && isAuthenticated && (
                                <div style={{ marginTop: '1rem' }}>
                                  <ShapRadarChart shapData={pick.shap} playerName={pick.playerName} forceBarLayout compact compactMaxFeatures={6} labelMaxLength={28} />
                                </div>
                              )}
                              {pick.shap && !isAuthenticated && (
                                <ModelInsightsLockedTeaser playerName={pick.playerName} variant="table" />
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
        )}
      </DesktopTableWrapper>
      </>
    </Container>
  );
};

export default HRPredictions;